/**
 * useWatchlist - Hook for progressive loading of watchlist data
 */

import { useState, useEffect, useCallback } from 'react';
import useAppStore from '../store/useAppStore.js';
import APIClient from '../api/client.js';
import CacheManager from '../utils/cacheManager.js';

export default function useWatchlist() {
  const [error, setError] = useState(null);

  const {
    tickers,
    isLoading,
    hasMore,
    totalTickers,
    market,
    setTickers,
    addTickers,
    setLoading,
    setHasMore,
    setTotalTickers,
    setCacheVersion,
    setLastUpdated
  } = useAppStore();

  // Initial load and re-fetch when market changes
  useEffect(() => {
    loadInitialBatch();
  }, [market]);

  const loadInitialBatch = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check cache first (market-specific cache key)
      const cacheKey = `watchlist_initial_${market}`;
      const cached = CacheManager.getLocal(cacheKey);
      if (cached) {
        console.log(`[useWatchlist] Loading ${market} from cache`);
        setTickers(cached.tickers);
        setTotalTickers(cached.total);
        setLastUpdated(cached.timestamp);

        // Background refresh
        loadFromAPI(0, 100, true);
        return;
      }

      // Load from API
      await loadFromAPI(0, 100);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadFromAPI = async (offset, limit, isBackgroundRefresh = false) => {
    try {
      const data = await APIClient.fetchWatchlist(limit, offset, market);

      if (offset === 0) {
        // Initial batch
        setTickers(data.tickers || []);
        setTotalTickers(data.total_tickers || 0);
        setHasMore(data.has_more || false);

        // Cache it (market-specific cache key)
        const cacheKey = `watchlist_initial_${market}`;
        CacheManager.setLocal(cacheKey, {
          tickers: data.tickers,
          total: data.total_tickers,
          timestamp: Date.now()
        });

        // Start background loading if there's more
        if (data.has_more) {
          setTimeout(() => loadNextBatch(100), 500);
        }
      } else {
        // Subsequent batches
        addTickers(data.tickers || []);
        setHasMore(data.has_more || false);

        // Continue loading if there's more
        if (data.has_more) {
          setTimeout(() => loadNextBatch(offset + limit), 100);
        }
      }

      setLastUpdated(Date.now());
    } catch (err) {
      if (!isBackgroundRefresh) {
        throw err;
      }
    }
  };

  const loadNextBatch = useCallback(async (offset) => {
    try {
      await loadFromAPI(offset, 200);
    } catch (err) {
      console.error('[useWatchlist] Background load error:', err);
    }
  }, []);

  const refresh = useCallback(async () => {
    CacheManager.removeLocal('watchlist_initial');
    await loadInitialBatch();
  }, []);

  return {
    tickers,
    isLoading,
    hasMore,
    totalTickers,
    error,
    refresh
  };
}
